import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { transfersApi } from '@/services/accountingService';

/**
 * Canonical list extraction: esponse.data.data || response.data accepted any
 * shape, so state.transfers could end up a non-array and break every
 * downstream .map/.length.
 */
const toTransferList = (response: any): any[] => {
  const payload = response?.data?.data ?? response?.data;
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.transfers) ? payload.transfers : [];
};

/**
 * rr.response.data is a raw server envelope; reduce it to a short,
 * serializable message so state.error always renders.
 */
const toSafeError = (err: any): string => {
  const payload = err?.response?.data;
  const message =
    (typeof payload === 'string' ? payload : payload?.message) ??
    (typeof err?.message === 'string' ? err.message : '');
  return message && message.length <= 300 ? message : 'An unexpected error occurred.';
};

interface TransfersState {
  transfers: any[];
  transfer: any | null;
  loading: boolean;
  error: any | null;
}

const initialState: TransfersState = {
  transfers: [],
  transfer: null,
  loading: false,
  error: null,
};

export const fetchTransfers = createAsyncThunk(
  'transfers/fetchTransfers',
  async (params: Record<string, any> | undefined, { rejectWithValue }) => {
    try {
      const response: any = await transfersApi.getAll(params || {});
      return toTransferList(response);
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

export const createTransfer = createAsyncThunk(
  'transfers/createTransfer',
  async (data: any, { rejectWithValue }) => {
    try {
      const response: any = await transfersApi.create(data);
      const created = response?.data?.data;
      if (!created) {
        return rejectWithValue(response?.data?.message || 'Failed to create transfer');
      }
      return created;
    } catch (err: any) {
      return rejectWithValue(toSafeError(err));
    }
  }
);

const transfersSlice = createSlice({
  name: 'transfers',
  initialState,
  reducers: {
    resetTransfers(state) {
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    const handlePending = (state: TransfersState) => {
      state.loading = true;
      state.error = null;
    };
    const handleRejected = (state: TransfersState, action: any) => {
      state.loading = false;
      state.error = action.payload;
    };

    builder
      .addCase(fetchTransfers.pending, handlePending)
      .addCase(fetchTransfers.fulfilled, (state, action) => {
        state.loading = false;
        const payload: any = action.payload;
        // Keep the state invariant: `transfers` is always an array, even if
        // the API answers with a paginated envelope.
        state.transfers = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.data)
            ? payload.data
            : [];
      })
      .addCase(fetchTransfers.rejected, handleRejected)

      .addCase(createTransfer.pending, handlePending)
      .addCase(createTransfer.fulfilled, (state, action) => {
        state.loading = false;
        state.transfers.unshift(action.payload);
      })
      .addCase(createTransfer.rejected, handleRejected);
  },
});

export const { resetTransfers } = transfersSlice.actions;

export const selectTransfers = (state: any) => state.transfers.transfers;
export const selectTransfer = (state: any) => state.transfers.transfer;
export const selectTransfersLoading = (state: any) => state.transfers.loading;
export const selectTransfersError = (state: any) => state.transfers.error;

export default transfersSlice.reducer;
